import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Logo, { detectBrand, BRAND_META } from "@/components/Logo";
import { ArrowRight, ShieldCheck, Camera, Clock } from "lucide-react";

export default function Landing() {
    const brand = detectBrand();
    const brandMeta = BRAND_META[brand] || BRAND_META.gardinbussen;
    const formPath = brand === "gardinfabrikken" ? "/gf/reklamation" : "/reklamation";
    return (
        <div className="min-h-screen bg-[#FAFAF9]">
            {/* Header */}
            <header className="border-b border-[#E5E1DA] bg-white/80 backdrop-blur-sm sticky top-0 z-10">
                <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Logo size="md" />
                </div>
            </header>

            {/* Hero */}
            <section className="relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-30"
                    style={{
                        backgroundImage:
                            "url('https://images.unsplash.com/photo-1758974775331-c5400bbef625?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBicmlnaHQlMjBsaXZpbmclMjByb29tJTIwY3VydGFpbnN8ZW58MHx8fHwxNzc3OTAwMjA4fDA&ixlib=rb-4.1.0&q=85')",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-br from-[#FAFAF9] via-[#FAFAF9]/70 to-transparent" />
                <div className="relative max-w-6xl mx-auto px-6 py-20 md:py-32">
                    <div className="max-w-2xl">
                        <div className="inline-block mb-6 px-3 py-1 rounded-full bg-[color:var(--brand-accent-soft)] text-[color:var(--brand-accent-strong)] text-xs font-semibold uppercase tracking-wider">
                            Sikker og nem reklamationshåndtering
                        </div>
                        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#2C2A29] leading-[1.05]">
                            Oplever du et problem med dit gardin?
                        </h1>
                        <p className="mt-6 text-lg text-[#5C5854] leading-relaxed">
                            Udfyld vores enkle reklamationsformular. Vi håndterer din reklamation inden for 1-3 hverdage.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <Link to={formPath}>
                                <Button
                                    size="lg"
                                    data-testid="start-complaint-button"
                                    className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-white rounded-lg px-8 py-6 text-base font-medium"
                                >
                                    Start reklamation
                                    <ArrowRight className="w-5 h-5 ml-2" />
                                </Button>
                            </Link>
                        </div>
                    </div>

                    <div className="mt-20 grid md:grid-cols-3 gap-6">
                        <FeatureCard
                            icon={<Clock className="w-5 h-5" />}
                            title="Tag 5 minutter"
                            body="Vores formular er bygget til at være hurtig og overskuelig – også på mobilen."
                        />
                        <FeatureCard
                            icon={<Camera className="w-5 h-5" />}
                            title="Upload billeder direkte"
                            body="Tag billeder med dit kamera og upload dem sikkert sammen med din reklamation."
                        />
                        <FeatureCard
                            icon={<ShieldCheck className="w-5 h-5" />}
                            title="Sikker behandling"
                            body="Du modtager et unikt sagsnummer og en bekræftelse på mail med det samme."
                        />
                    </div>
                </div>
            </section>

            <footer className="border-t border-[#E5E1DA] py-8">
                <div className="max-w-6xl mx-auto px-6 text-sm text-[#78736E] text-center">
                    © {new Date().getFullYear()} {brandMeta.name} · Reklamationssystem
                </div>
            </footer>
        </div>
    );
}

function FeatureCard({ icon, title, body }) {
    return (
        <div className="bg-white border border-[#E5E1DA] rounded-xl p-6 hover:-translate-y-px hover:shadow-md transition-all duration-200">
            <div className="w-10 h-10 rounded-lg bg-[color:var(--brand-accent-soft)] text-[color:var(--brand-accent-strong)] flex items-center justify-center mb-4">
                {icon}
            </div>
            <div className="font-heading text-base font-medium text-[#2C2A29] mb-2">{title}</div>
            <div className="text-sm text-[#5C5854] leading-relaxed">{body}</div>
        </div>
    );
}
